import { HubResource, Kits, Templates, HubResourceSection, HubResourceId, DiscoverItemType } from 'utils/gloo-types';
import { getAllKits, getAllTemplates } from 'actions/requests';
import Select from 'components/molecules/Select';
import { useContext, useEffect, useState } from 'react';
import { discoverItemCardForKit, discoverItemCardForTemplate, hubResourceSelectOptions } from 'utils/discover';
import { useThunkDispatch } from 'utils/reduxHook';
import { useIsMountedRef } from 'utils/hooks';
import { union } from 'lodash';
import Button from 'components/atoms/Button';
import { ActionType } from 'pages/_AdminAuthoringPages/AdminHubAuthoring/action';
import { HubAuthoringDispatchContext } from 'pages/_AdminAuthoringPages/AdminHubAuthoring/reducer';
import Typography from 'components/atoms/Typography';
import DiscoverCardPreview from 'pages/_AdminAuthoringPages/_components/DiscoverCardPreview';
import { SortDirection } from 'pages/_AdminAuthoringPages/_components/HubSectionForm';

interface HubAuthoringResourcesSectionContentProps {
  section: HubResourceSection & { clientId: string };
  index: number;
}

function HubAuthoringResourcesSectionContent({ section, index }: HubAuthoringResourcesSectionContentProps) {
  const globalDispatch = useThunkDispatch();
  const dispatch = useContext(HubAuthoringDispatchContext);
  const [allHubResources, setAllHubResources] = useState<HubResource[]>([]);
  const isMountedRef = useIsMountedRef();
  const resourceIds = section.resourceIds;
  const [filteredResources, setFilteredResources] = useState<HubResource[]>([]);

  const hubResourceForKit = (kit: Kits) => {
    return {
      type: DiscoverItemType.KIT,
      kit,
    };
  };

  const hubResourceForTemplate = (template: Templates) => {
    return {
      type: DiscoverItemType.TEMPLATE,
      template,
    };
  };

  const hubResourceIdToAdd = (hubResource: HubResource): HubResourceId => {
    switch (hubResource.type) {
      case DiscoverItemType.KIT:
        return {
          id: hubResource.kit.id,
          type: DiscoverItemType.KIT,
        };
      case DiscoverItemType.TEMPLATE:
        return {
          id: hubResource.template.id,
          type: DiscoverItemType.TEMPLATE,
        };
    }
  };

  const discoverItemForHubResource = (hubResource: HubResource) => {
    switch (hubResource.type) {
      case DiscoverItemType.KIT:
        return discoverItemCardForKit(hubResource.kit);
      case DiscoverItemType.TEMPLATE:
        return discoverItemCardForTemplate(hubResource.template);
    }
  };

  const gatherFilteredResources = (hubResources: HubResource[]) => {
    const result = resourceIds.map((resourceId: HubResourceId) => {
      const type = resourceId.type;
      return hubResources.find((hubResource: HubResource) => {
        return hubResource.type === resourceId.type && hubResource[type].id === resourceId.id;
      });
    });
    setFilteredResources(result);
  };

  useEffect(() => {
    const loadTemplatesAndKits = async () => {
      const allTemplates = await globalDispatch(getAllTemplates());
      const allKits = await globalDispatch(getAllKits());
      if (isMountedRef.current) {
        const allResources = union<HubResource>(
          allKits.map((kit) => hubResourceForKit(kit)) as Array<HubResource>,
          allTemplates.map((template) => hubResourceForTemplate(template)) as Array<HubResource>,
        );
        setAllHubResources(allResources);
        gatherFilteredResources(allResources);
      }
    };
    loadTemplatesAndKits();
  }, [isMountedRef]);

  useEffect(() => {
    if (allHubResources.length > 0) {
      gatherFilteredResources(allHubResources);
    }
  }, [resourceIds]);

  const unusedResources = allHubResources.filter((hubResource) => {
    return !filteredResources.includes(hubResource);
  });

  return (
    <>
      {section.resourceIds.length < 12 ? (
        <Select
          dataDurableId="kitResourcesListSelectId"
          label="Add Resources (Limit 12 Resources Per Section)"
          options={hubResourceSelectOptions(unusedResources)} // Filter allHubResources to include only unused resources
          onChange={(resourceSelected) => {
            const hubResourceId: HubResourceId = hubResourceIdToAdd(resourceSelected.value);
            dispatch({
              type: ActionType.addSectionResource,
              payload: {
                index,
                hubResourceId,
              },
            });
          }}
          placeholder="Choose Resource"
          className="mb-4"
        />
      ) : (
        <Typography variant="sm" className="text-glooCharcoal font-bold pb-5">
          The maximum number of resources per section is 12. Remove at least one resource before adding another.
        </Typography>
      )}

      <div className="flex flex-row flex-wrap">
        {filteredResources.map((hubResource, i) => {
          const item = discoverItemForHubResource(hubResource);
          const isLast = i === resourceIds.length - 1;
          if (!item) return undefined;
          return (
            <div key={i} className="relative w-full px-2 pb-8 md:w-1/2 lg:w-1/3 xl:w-1/4 2xl:w-1/5">
              <DiscoverCardPreview
                key={i}
                itemCard={item}
                className="w-full h-full"
                onClick={() => {
                  // Noop
                }}
              />
              <div className="flex flex-row absolute top-1 right-3">
                <Button
                  onClick={() => {
                    dispatch({
                      type: ActionType.orderSectionResourceIds,
                      payload: {
                        sectionIndex: index,
                        resourceIdIndex: i,
                        isLast,
                        sortDirection: SortDirection.Up,
                      },
                    });
                  }}
                  icon="arrow-left"
                  dataDurableId="hubResourceDemoteButton"
                  size="xs"
                  type="white"
                  buttonType="button"
                  hideHighlight
                />
                <Button
                  onClick={() => {
                    dispatch({
                      type: ActionType.orderSectionResourceIds,
                      payload: {
                        sectionIndex: index,
                        resourceIdIndex: i,
                        isLast,
                        sortDirection: SortDirection.Down,
                      },
                    });
                  }}
                  icon="arrow-right"
                  dataDurableId="hubResourcePromoteButton"
                  size="xs"
                  type="white"
                  buttonType="button"
                  hideHighlight
                  className="mx-1"
                />
                <Button
                  onClick={() => {
                    dispatch({
                      type: ActionType.deleteSectionResourceId,
                      payload: {
                        sectionIndex: index,
                        resourceIdIndex: i,
                      },
                    });
                  }}
                  icon="trash"
                  dataDurableId="hubResourceRemoveButton"
                  size="xs"
                  type="destruction"
                  hideHighlight
                  buttonType="button"
                />
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

export default HubAuthoringResourcesSectionContent;
